import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type BrandLockupTone = 'light' | 'dark';
export type BrandLockupSize = 'sm' | 'md' | 'lg';

/**
 * Circular NPR seal plus registry wordmark, matching the public website.
 */
@Component({
  selector: 'app-npr-brand-lockup',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex items-center gap-2.5 min-w-0">
      <div [class]="sealClass()">
        <img
          [src]="src()"
          [alt]="decorative() ? '' : 'News and Publication Registry'"
          class="size-full object-cover"
          [attr.width]="sealPx()"
          [attr.height]="sealPx()"
        />
      </div>
      <div class="min-w-0 hidden sm:block">
        <p [class]="titleClass()">NEWS AND PUBLICATION REGISTRY</p>
        <p [class]="taglineClass()">Publish • Inform • Verify</p>
      </div>
    </div>
  `,
})
export class NprBrandLockupComponent {
  readonly tone = input<BrandLockupTone>('dark');
  readonly size = input<BrandLockupSize>('md');
  readonly src = input('/images/npr-favicon.png');
  readonly decorative = input(false);

  sealPx(): number {
    switch (this.size()) {
      case 'sm':
        return 40;
      case 'lg':
        return 56;
      default:
        return 48;
    }
  }

  sealClass(): string {
    const size =
      this.size() === 'sm'
        ? 'size-10'
        : this.size() === 'lg'
          ? 'size-11 sm:size-12 lg:size-14'
          : 'size-11 sm:size-12';
    return `${size} shrink-0 rounded-full overflow-hidden bg-white ring-1 ${
      this.tone() === 'dark' ? 'ring-white/30' : 'ring-slate-200'
    }`;
  }

  titleClass(): string {
    const size = this.size() === 'lg' ? 'text-sm lg:text-base' : 'text-sm';
    const color = this.tone() === 'dark' ? 'text-white' : 'text-forest-800';
    return `${color} font-bold ${size} leading-tight tracking-wide truncate`;
  }

  taglineClass(): string {
    const color = this.tone() === 'dark' ? 'text-teal-200' : 'text-forest-600';
    return `${color} text-[10px] sm:text-xs tracking-widest truncate`;
  }
}
